import { useCallback, useEffect, useRef, useState } from "react";
import { Wind, Pause } from "lucide-react";

const GLIDE_MS = 1100;
const DRIFT_PX = 20;
const MIN_GAP = 40;
const IGNORE = "button,a,input,textarea,select,label,form,[role=menuitem],[role=menu]";

const cosEase = (t) => 0.5 - 0.5 * Math.cos(Math.PI * t);
const outEase = (t) => 1 - Math.pow(1 - t, 3);
const absTop = (el) => el.getBoundingClientRect().top + window.scrollY;
const maxScroll = () => document.documentElement.scrollHeight - window.innerHeight;
const scrollToY = (y) => window.scrollTo({ top: y, behavior: "instant" });

// Dwell at reading pace: base time plus per-word time from the panel caption.
const dwellFor = (words) => Math.min(11000, Math.max(4500, 3800 + words * 380));

function buildStops(laneEl, selector, offset = 0) {
  const vh = window.innerHeight;
  const stops = [];
  laneEl.querySelectorAll(selector).forEach((el) => {
    const top = absTop(el) - offset;
    const h = el.offsetHeight;
    const words = el.dataset.words != null ? Number(el.dataset.words) : (el.querySelector("img")?.alt || "").split(/\s+/).filter(Boolean).length;
    const parts = Math.max(1, Math.ceil(h / (vh * 0.85)));
    for (let i = 0; i < parts; i++) {
      stops.push({
        y: top + (i === 0 ? 0 : Math.min(h - vh, (h / parts) * i)),
        dwell: dwellFor(words) / parts,
        pause: i === 0 && el.dataset.driftPause === "1",
      });
    }
  });
  stops.sort((a, b) => a.y - b.y);
  return stops.filter((s, i) => i === 0 || s.y - stops[i - 1].y > MIN_GAP);
}

export function useKahaniDrift(laneRef, endRef, resetKey, { panelSelector = '[data-testid^="reader-panel-"]', offset = 0 } = {}) {
  const [state, setState] = useState("off"); // off | drifting | paused
  const raf = useRef(0);
  const phase = useRef(null);
  const stateRef = useRef("off");

  const set = (s) => {
    stateRef.current = s;
    setState(s);
  };

  const endY = () => (endRef.current ? Math.min(maxScroll(), absTop(endRef.current) - 72) : maxScroll());

  const startDwell = useCallback((y, dwell) => {
    phase.current = { kind: "dwell", from: y, start: performance.now(), dur: dwell };
  }, []);

  const tick = useCallback(
    (now) => {
      const p = phase.current;
      if (!p || stateRef.current !== "drifting") return;
      const t = Math.min(1, (now - p.start) / p.dur);
      if (p.kind === "dwell") {
        scrollToY(Math.min(endY(), p.from + DRIFT_PX * outEase(t)));
        if (t >= 1) {
          const cur = window.scrollY;
          const end = endY();
          const next = laneRef.current && buildStops(laneRef.current, panelSelector, offset).find((s) => s.y > cur + MIN_GAP && s.y < end);
          phase.current = { kind: "glide", from: cur, to: next ? next.y : end, dwell: next?.dwell, pause: next?.pause, last: !next, start: now, dur: GLIDE_MS };
        }
      } else {
        scrollToY(p.from + (p.to - p.from) * cosEase(t));
        if (t >= 1) {
          if (p.last) {
            phase.current = null;
            set("off");
            return;
          }
          if (p.pause) {
            phase.current = null;
            set("paused");
            return;
          }
          startDwell(p.to, p.dwell);
        }
      }
      raf.current = requestAnimationFrame(tick);
    },
    [laneRef, startDwell] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const play = useCallback(() => {
    if (!laneRef.current) return;
    if (window.scrollY >= endY() - 2) return;
    const first = buildStops(laneRef.current, panelSelector, offset).find((s) => Math.abs(s.y - window.scrollY) < MIN_GAP);
    startDwell(window.scrollY, first ? first.dwell : 4500);
    set("drifting");
    cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(tick);
  }, [laneRef, startDwell, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const pause = useCallback(() => {
    cancelAnimationFrame(raf.current);
    phase.current = null;
    if (stateRef.current === "drifting") set("paused");
  }, []);

  const toggle = useCallback(() => (stateRef.current === "drifting" ? pause() : play()), [pause, play]);

  // Tap anywhere on the reader toggles; manual scroll input or hidden tab pauses.
  useEffect(() => {
    if (state === "off") return;
    let downTarget = null;
    const onDown = (e) => {
      downTarget = e.target;
    };
    const onClick = (e) => {
      const t = downTarget || e.target;
      downTarget = null;
      if (t === document.documentElement || t.closest(IGNORE)) return;
      toggle();
    };
    const onManual = () => stateRef.current === "drifting" && pause();
    const onKey = (e) => ["ArrowDown", "ArrowUp", "PageDown", "PageUp", " ", "j", "k", "Home", "End"].includes(e.key) && onManual();
    const onVis = () => document.hidden && pause();
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("click", onClick);
    window.addEventListener("wheel", onManual, { passive: true });
    window.addEventListener("touchmove", onManual, { passive: true });
    window.addEventListener("keydown", onKey);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("click", onClick);
      window.removeEventListener("wheel", onManual);
      window.removeEventListener("touchmove", onManual);
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [state, toggle, pause]);

  useEffect(() => {
    cancelAnimationFrame(raf.current);
    phase.current = null;
    set("off");
    return () => cancelAnimationFrame(raf.current);
  }, [resetKey]);

  return { state, toggle };
}

export const KahaniDriftButton = ({ state, onToggle, testId = "kahani-drift-btn", compact = false }) => {
  const paused = state === "paused";
  const active = state === "drifting";
  return (
    <button
      type="button"
      data-testid={testId}
      data-state={state}
      aria-pressed={active}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      className={`inline-flex items-center gap-2 rounded-full font-semibold transition-[background-color,transform,box-shadow] active:scale-95 ${
        compact ? "px-4 py-2 text-xs" : "px-6 py-3 min-h-[44px] text-sm"
      } ${
        paused
          ? "bg-white/10 text-white border border-white/20"
          : "bg-[#e61c5d] text-white hover:bg-[#ff2a6d] shadow-[0_0_0_4px_rgba(230,28,93,0.18)]"
      }`}
    >
      {paused ? <Pause className="w-4 h-4" /> : <Wind className={`w-4 h-4 ${active ? "animate-pulse" : ""}`} />}
      {paused ? "Paused" : "Kahani Drift"}
    </button>
  );
};
